import {
  PermissionContext,
  permissionsOperations,
} from '@ardoq/access-control';
import { presentationApi } from '@ardoq/api';
import {
  APIComponentAttributes,
  ArdoqId,
  DrilldownScopeData,
  type PersonalSettingMap,
  SelectedAggregate,
} from '@ardoq/api-types';
import { ButtonType } from '@ardoq/button';
import { isTruthy, toByIdDictionary } from '@ardoq/common-helpers';
import { DropdownItem, DropdownOptionType } from '@ardoq/dropdown-menu';
import { FeatureContext, featureOperations, Features } from '@ardoq/features';
import { IconName } from '@ardoq/icons';
import {
  toTopBarActionButtonProps,
  toTopBarDropdownProps,
  toTopBarIconButtonProps,
} from '@ardoq/page-layout';
import {
  AggregatesResultsState,
  keepValidFilters,
  ReportEventLocations,
  ReportExportType,
  reportReaderNewFeaturesNotificationsData,
  ReportReaderProps,
  trackExportReport,
  trackOpenComponentInWorkspace,
  trackOpenComponentOverviewPage,
} from '@ardoq/report-reader';
import { dispatchAction } from '@ardoq/rxbeach';
import { NewFeatureNotificationProps } from '@ardoq/snowflakes';
import { closeChatWindow, openChatWindow } from 'assistant/actions';
import { AssistantState } from 'assistant/assistant$';
import { ASSISTANT_ON_TOP } from 'assistant/constants';
import { saveExcelBlob } from '@ardoq/dom-utils';
import { isEmpty } from 'lodash';
import { componentAccessControlOperation } from 'resourcePermissions/accessControlHelpers/component';
import { workspaceAccessControlInterface } from 'resourcePermissions/accessControlHelpers/workspace';
import { navigate } from 'router/declarative/streamRouterAdapter/actions';
import { componentInWorkspaceNavigationRequested } from 'streams/workspaces/actions';
import { trackEvent } from 'tracking/tracking';
import { toLinkProps } from '../../router/declarative/toLinkProps';
import { pathTo } from '../../router/declarative/pathTo';
import { NavbarProps } from '../../views/navbar/NavbarView';
import { getDataAnalysisHelpItems } from '../../views/navbar/helpItems/getDataAnalysisHelpItems';
import { t, type I18nLocale } from '@ardoq/i18n';
import {
  drilldownWasTriggered,
  reportReaderNewFeatureNotificationClosed,
} from './actions';
import { NavBarButtonProps } from './NavBarButtons';
import {
  FetchPresentationSearchResultParams,
  FetchPresentationSearchResults,
  ReportReader$State,
} from './types';
import { openComponentOverviewDrawer } from 'componentOverview2025/componentOverview2025Drawer/openComponentOverviewDrawer';
import { ComponentLinkHandler } from '@ardoq/renderers';
import { getFeatureContext } from 'streams/featureContext$';
import { TrackingLocation } from '@ardoq/data-model';

export const exportToExcel = (exportData: Blob, reportName: string) => {
  saveExcelBlob(exportData, reportName);
  trackExportReport(
    trackEvent,
    ReportEventLocations.ARDOQ,
    ReportExportType.EXCEL
  );
};

export const exportToReadableExcel = (exportData: Blob, reportName: string) => {
  saveExcelBlob(exportData, reportName);
  trackExportReport(
    trackEvent,
    ReportEventLocations.ARDOQ,
    ReportExportType.EXCEL_READABLE
  );
};

export const exportToJSON = (reportId: ArdoqId) => {
  window.open(`/api/report/${reportId}/export`);
  trackExportReport(
    trackEvent,
    ReportEventLocations.ARDOQ,
    ReportExportType.JSON
  );
};

export const getHasComponentReadPermission = (
  scopeData: DrilldownScopeData,
  permissionContext: PermissionContext
) => {
  const componentsById = toByIdDictionary(scopeData.components);

  return (entityId: ArdoqId) =>
    componentAccessControlOperation.canAccessComponent({
      component: componentsById[entityId],
      permissionContext,
    });
};

const getComponentOverviewPageLink = (
  componentId: ArdoqId,
  hasComponentOverview2025: boolean,
  trackingLocation: TrackingLocation,
  i18nLocale: I18nLocale
): ComponentLinkHandler => ({
  href: pathTo('/component-overview/:componentId', { componentId }),
  tooltipText: t(
    i18nLocale,
    'report:reader.componentLink.tooltip',
    'Go to component overview'
  ),
  onClick: () => {
    if (trackingLocation === 'reportReader') {
      trackOpenComponentOverviewPage(trackEvent, ReportEventLocations.ARDOQ);
    }
    openOrNavigateToComponentOverview(
      componentId,
      hasComponentOverview2025,
      trackingLocation
    );
  },
});

const openOrNavigateToComponentOverview = (
  componentId: ArdoqId,
  hasComponentOverview2025: boolean,
  trackingLocation: TrackingLocation
) => {
  // Team Navigation - for homepage we don't want to show component in a drawer
  if (hasComponentOverview2025 && trackingLocation !== 'homePage') {
    openComponentOverviewDrawer({
      componentId,
      trackingLocation,
      startTab: 'overview',
    });
  } else {
    dispatchAction(
      navigate({
        route: '/component-overview/:componentId',
        params: { componentId },
      })
    );
  }
};

export const getComponentLinkHandler = (
  componentId: ArdoqId,
  i18nLocale: I18nLocale,
  trackingLocation: TrackingLocation = 'reportReader'
) => {
  // todo explore possibility to pass this as a prop
  const featureContext = getFeatureContext();
  return getComponentOverviewPageLink(
    componentId,
    featureOperations.hasFeature(
      featureContext,
      Features.COMPONENT_OVERVIEW_2025
    ),
    trackingLocation,
    i18nLocale
  );
};

export const prepareGetComponentNavigationOptions =
  (
    permissionContext: PermissionContext,
    hasComponentOverview2025: boolean,
    i18nLocale: I18nLocale
  ): ((component: APIComponentAttributes) => DropdownItem[]) =>
  component => {
    const isOrgContributor =
      permissionsOperations.hasOrgContributorRole(permissionContext);
    const entries: DropdownItem[] = [];
    if (isOrgContributor) {
      // If the user is a contributor, only the default option is allowed
      // (Component Overview Page)
      return entries;
    }
    if (
      componentAccessControlOperation.canAccessComponent({
        component,
        permissionContext,
      })
    ) {
      entries.push({
        label: t(
          i18nLocale,
          'report:reader.componentNavigation.componentOverviewPage',
          'Component Overview Page'
        ),
        iconName: IconName.COMPONENT,
        type: DropdownOptionType.BUTTON_OPTION,
        onClick: () => {
          trackOpenComponentOverviewPage(
            trackEvent,
            ReportEventLocations.ARDOQ
          );
          openOrNavigateToComponentOverview(
            component._id,
            hasComponentOverview2025,
            'reportReader'
          );
        },
      });
    }

    if (
      workspaceAccessControlInterface.canAccessWorkspace(
        permissionContext,
        component.rootWorkspace
      )
    ) {
      entries.push({
        label: t(
          i18nLocale,
          'report:reader.componentNavigation.workspace',
          'Workspace'
        ),
        iconName: IconName.WORKSPACE,
        type: DropdownOptionType.BUTTON_OPTION,
        onClick: () => {
          trackOpenComponentInWorkspace(trackEvent, ReportEventLocations.ARDOQ);
          dispatchAction(
            componentInWorkspaceNavigationRequested({
              componentId: component._id,
              trackingLocation: 'reportReader',
            })
          );
        },
      });
    }

    return entries;
  };

export const fetchPresentationSearchResults: FetchPresentationSearchResults =
  async ({
    pagination,
    sort,
    filters,
    reportId,
    slideId,
    presentationId,
  }: FetchPresentationSearchResultParams) => {
    dispatchAction(drilldownWasTriggered(true));
    const searchResults = await presentationApi.search(
      presentationId!,
      reportId,
      slideId,
      {
        from: pagination.from,
        size: pagination.size,
        sort: sort.columnKey ?? undefined,
        order: sort.order ?? undefined,
        filters: isEmpty(filters) ? undefined : filters,
      }
    );
    dispatchAction(drilldownWasTriggered(false));
    return searchResults;
  };

export const keepValidReaderStateFilters = (
  state: ReportReader$State
): ReportReader$State => {
  const validFilters = state.filters
    ? keepValidFilters(state.filters, state.columns)
    : null;
  return { ...state, filters: validFilters };
};

export const getNewFeatureNotificationsProps = (
  personalSettings: Partial<PersonalSettingMap>
): NewFeatureNotificationProps[] =>
  reportReaderNewFeaturesNotificationsData.reduce<
    NewFeatureNotificationProps[]
  >((notificationsArray, { notificationKey, notificationMessage }) => {
    notificationsArray.push({
      notificationMessage,
      show: !personalSettings[notificationKey as keyof PersonalSettingMap],
      onClose: () =>
        dispatchAction(
          reportReaderNewFeatureNotificationClosed(notificationKey)
        ),
    });
    return notificationsArray;
  }, []);

export const getAggregatesResultsState = ({
  selectedAggregate,
  aggregatesResultsState,
  totalNumberOfRows,
}: {
  selectedAggregate: SelectedAggregate;
  totalNumberOfRows: number;
  aggregatesResultsState: AggregatesResultsState;
}): AggregatesResultsState => {
  if (!selectedAggregate) {
    return {
      aggregateResults: { aggregates: null, totalNumberOfRows },
      aggregateError: null,
    };
  }
  return aggregatesResultsState;
};

export const getNewNavBarProps = ({
  reportName,
  reportId,
  navBarButtonProps,
  i18nLocale,
}: {
  reportName: string;
  reportId: ArdoqId;
  navBarButtonProps: NavBarButtonProps;
  i18nLocale: I18nLocale;
}): NavbarProps['newNavBarProps'] => {
  const { exportButtonProps, createBroadcastButtonProps, toolbarButtonProps } =
    navBarButtonProps;
  const {
    copyUrlButtonProps,
    shareButtonProps,
    editMenuButtonProps,
    backToOpenDashboardButtonProps,
  } = toolbarButtonProps;
  return {
    helpItems: getDataAnalysisHelpItems(i18nLocale),
    breadcrumbs: [
      {
        ...toLinkProps({ route: '/report', params: {} }),
        label: t(i18nLocale, 'report:reader.breadcrumb.reports', 'Reports'),
      },
      {
        ...toLinkProps({
          route: '/report/reader/:reportId',
          params: { reportId },
        }),
        label: reportName,
      },
    ].filter(isTruthy),
    actionItems: [
      backToOpenDashboardButtonProps.onClick && {
        ...toTopBarActionButtonProps({
          label: t(
            i18nLocale,
            'report:reader.toolbar.backToDashboard',
            'Back to open Dashboard'
          ),
          icon: { name: IconName.CHEVRON_LEFT },
          buttonType: ButtonType.GHOST,
          onClick: backToOpenDashboardButtonProps.onClick,
        }),
      },
      copyUrlButtonProps &&
        copyUrlButtonProps.hasCopyArdoqURLOption &&
        copyUrlButtonProps.hasNewDiscoverCoreNavigation && {
          ...toTopBarIconButtonProps({
            label: copyUrlButtonProps.labels.copyArdoqEntityUrl,
            onClick: copyUrlButtonProps.copyArdoqEntityUrl,
            icon: { name: IconName.URL },
          }),
        },
      copyUrlButtonProps &&
        !copyUrlButtonProps.hasNewDiscoverCoreNavigation &&
        copyUrlButtonProps.hasCopyArdoqURLOption && {
          ...toTopBarDropdownProps({
            label: copyUrlButtonProps.labels.tooltipText,
            isIconOnly: true,
            options: [
              copyUrlButtonProps.hasCopyArdoqURLOption && {
                type: DropdownOptionType.OPTION,
                label: copyUrlButtonProps.labels.copyArdoqEntityUrl,
                onClick: copyUrlButtonProps.copyArdoqEntityUrl,
              },
              {
                type: DropdownOptionType.OPTION,
                label: copyUrlButtonProps.labels.copyDiscoverEntityUrl,
                onClick: copyUrlButtonProps.copyDiscoverEntityUrl,
              },
            ].filter(isTruthy),
            icon: { name: IconName.URL },
          }),
        },
      createBroadcastButtonProps.isShown && {
        ...toTopBarIconButtonProps({
          label: t(
            i18nLocale,
            'report:reader.more.createBroadcast',
            'Create Broadcast'
          ),
          icon: { name: IconName.BROADCAST },
          state: createBroadcastButtonProps.isDisabled ? 'disabled' : 'idle',
          onClick: createBroadcastButtonProps.onClick,
        }),
      },
      editMenuButtonProps.isShown && {
        ...toTopBarDropdownProps({
          label: t(i18nLocale, 'report:reader.toolbar.editButton', 'Edit'),
          options: editMenuButtonProps.options,
          buttonType: ButtonType.SECONDARY,
        }),
      },
      exportButtonProps.options.length > 0 && {
        ...toTopBarDropdownProps({
          label: t(i18nLocale, 'report:reader.exportButton.label', 'Save as'),
          options: exportButtonProps.options,
          buttonType: ButtonType.PRIMARY,
          state: exportButtonProps.isLoading ? 'loading' : 'idle',
        }),
      },
      toTopBarActionButtonProps({
        buttonType: ButtonType.TERTIARY,
        icon: { name: IconName.PERSON },
        label: shareButtonProps.label,
        onClick: shareButtonProps.onClick,
      }),
    ].filter(isTruthy),
  };
};

export const getAIButtonProps = (
  featureContext: FeatureContext,
  mode: AssistantState['mode']
): ReportReaderProps['aiButtonProps'] => {
  if (ASSISTANT_ON_TOP) return undefined;

  const hasAIChat = featureOperations.hasFeature(
    featureContext,
    Features.AI_CHAT
  );

  if (!hasAIChat) return undefined;

  return {
    isActive: mode === 'open',
    onClick: () => {
      if (mode === 'open') {
        dispatchAction(closeChatWindow());
      } else {
        dispatchAction(openChatWindow());
      }
    },
  };
};
